/**
 * Shared email variable configuration.
 *
 * Single source of truth for available template variables in email sequences.
 * Used by the email body editor, sequence preview, and review emails step.
 */

// Mirrors the user-pickable subset of
// server/emailbison/usecases/variable_resolution_utils.py AVAILABLE_VARIABLES.
// NOTE: the backend set additionally includes LOCATION, which is intentionally
// excluded here — it's consumed only by the geography_classifier engine and
// is not surfaced in the user-facing variable picker for the POC.
export const AVAILABLE_VARIABLES = [
  { value: '{FIRST_NAME}', label: 'First Name', token: 'FIRST_NAME' },
  { value: '{LAST_NAME}', label: 'Last Name', token: 'LAST_NAME' },
  { value: '{EMAIL}', label: 'Email', token: 'EMAIL' },
  { value: '{COMPANY}', label: 'Company', token: 'COMPANY' },
  { value: '{TITLE}', label: 'Title', token: 'TITLE' },
] as const

export type VariableToken = (typeof AVAILABLE_VARIABLES)[number]['token']

/**
 * Replace {VARIABLE} tokens in text with values from the token map.
 *
 * @param text - Template text with {VARIABLE} placeholders
 * @param tokens - Map of variable name to resolved value
 * @param options.escapeValues - If true, HTML-escape values before substitution
 */
export function fillVariables(
  text: string,
  tokens: Record<string, string>,
  options?: { escapeValues?: boolean }
): string {
  return text.replace(/\{([A-Za-z_][A-Za-z0-9_]*)\}/g, (match, key) => {
    const value = tokens[key]
    if (value === undefined) return match
    const replacement = options?.escapeValues ? escapeHtml(value) : value
    return replacement
  })
}

/**
 * Find any {variable_name} placeholders in text that haven't been resolved.
 * This deliberately catches malformed placeholders too, since `{123}` and
 * `{MENU-ITEM}` cannot be safely filled at send time.
 */
export function detectUnfilledVariables(text: string): string[] {
  return Array.from(new Set(text.match(/\{[^{}]+\}/g) ?? []))
}

export function findUnknownTemplateVariables(
  variables: ReadonlyArray<EmailVariable>,
  texts: ReadonlyArray<string | null | undefined>
): string[] {
  const knownTokens = new Set(variables.map((variable) => variable.token))
  const foundTokens = texts.flatMap((text) =>
    typeof text === 'string' ? detectUnfilledVariables(text) : []
  )
  return Array.from(
    new Set(
      foundTokens.filter((token) => {
        const tokenName = token.replace(/[{}]/g, '')
        return !knownTokens.has(tokenName)
      })
    )
  )
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

/**
 * Shape of a single entry in the email variable picker. This covers both
 * built-in `AVAILABLE_VARIABLES` items and campaign-scoped AI/manual columns.
 */
export interface EmailVariable {
  value: string // e.g. '{SNIPPED_COMPANY}'
  label: string // human-readable column name
  token: string // e.g. 'SNIPPED_COMPANY'
  isAI?: boolean // true for campaign-scoped AI columns
  isCampaignColumn?: boolean // true for campaign-scoped custom/manual columns
  isManual?: boolean
  missingCount?: number
  totalCount?: number
  hasMissingValues?: boolean
}

/**
 * Merge built-in variables with campaign-specific column variables.
 * Built-ins come first, then custom in definition order.
 */
export function mergeVariables(
  custom: ReadonlyArray<EmailVariable> = []
): ReadonlyArray<EmailVariable> {
  return [...AVAILABLE_VARIABLES, ...custom]
}

export function findCampaignColumnVariablesWithMissingValues(
  variables: ReadonlyArray<EmailVariable>,
  texts: ReadonlyArray<string | null | undefined>
): ReadonlyArray<EmailVariable> {
  return variables.filter(
    (variable) =>
      variable.isCampaignColumn &&
      variable.hasMissingValues &&
      texts.some((text) => {
        if (typeof text !== 'string') return false
        return detectUnfilledVariables(text).some(
          (token) => token.replace(/[{}]/g, '') === variable.token
        )
      })
  )
}

export function getMissingValuesLabel(variable: EmailVariable): string {
  if (
    typeof variable.missingCount === 'number' &&
    typeof variable.totalCount === 'number'
  ) {
    return `${variable.missingCount}/${variable.totalCount} missing`
  }
  return 'Missing values'
}

export function getMissingValuesMessage(variable: EmailVariable): string {
  if (
    typeof variable.missingCount === 'number' &&
    typeof variable.totalCount === 'number'
  ) {
    return `${variable.label} is missing values for ${variable.missingCount} of ${variable.totalCount} contacts. Fill those cells before sending.`
  }
  return `${variable.label} is missing values for some contacts. Fill those cells before sending.`
}
